import { useCallback, useMemo } from 'react';
import DownFilled from '@distribusion/portal-icons/dist/DownFilled';
import { useSession } from '@distribusion/portal-lib/auth';
import { CountryFlag, Flex, Text } from '@distribusion/portal-lib/components';
import { DEFAULT_LANGUAGE, LanguageEnum } from '@distribusion/portal-lib/constants';
import { Button, Dropdown, MenuProps, Space } from 'antd';
import type { MenuItemType } from 'antd/es/menu/hooks/useItems';
import { useUpdateUserPreferences } from 'features/users/api/users';
import { COUNTRY_CODES, LANGUAGE_NAMES, LANGUAGE_OPTIONS } from 'features/users/constants';
import { useTranslation } from 'lib/translation';
import { useMount } from 'utils/use-mount';

import './language-switch.less';

const items: MenuItemType[] = LANGUAGE_OPTIONS.map((lang) => ({
  key: lang,
  label: (
    <Space>
      <CountryFlag countryCode={COUNTRY_CODES[lang]} />
      <Text>{LANGUAGE_NAMES[lang]}</Text>
    </Space>
  ),
}));

export const LanguageSwitch = () => {
  const { i18n } = useTranslation();
  const { data: sessionData } = useSession();
  const { mutate: updateUserPreferences } = useUpdateUserPreferences();

  const setDefaultLanguage = useCallback(() => {
    const metadata = sessionData.identity?.metadata_public || {};
    if (
      'language' in metadata &&
      LANGUAGE_OPTIONS.includes(metadata.language as LanguageEnum) &&
      i18n.language !== metadata.language
    ) {
      i18n.changeLanguage(metadata.language as LanguageEnum);
    }
  }, [i18n, sessionData.identity?.metadata_public]);

  useMount(setDefaultLanguage);

  const onClick: MenuProps['onClick'] = useCallback(
    ({ key }) => {
      i18n.changeLanguage(key);
      updateUserPreferences({
        id: sessionData.identity.id,
        language: key as LanguageEnum,
      });
    },
    [i18n, sessionData.identity.id, updateUserPreferences],
  );

  const selectedItem = useMemo(
    () =>
      items.find((item) => item.key === i18n.language) ||
      ({ key: DEFAULT_LANGUAGE } as MenuItemType),
    [i18n.language],
  );

  return (
    <Dropdown
      menu={{
        items,
        selectable: true,
        selectedKeys: [selectedItem.key as string],
        onClick,
      }}
      trigger={['click']}
      overlayClassName="language-switch"
    >
      <Button type="text" size="large">
        <Flex gap={16}>
          <CountryFlag
            style={{ width: '24px' }}
            countryCode={COUNTRY_CODES[selectedItem.key as LanguageEnum]}
          />
          <DownFilled />
        </Flex>
      </Button>
    </Dropdown>
  );
};
